/* eslint-disable tailwindcss/no-custom-classname */
import React from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import './delivery-options-drawer.scss';
import { Drawer, DrawerHeader, DrawerBody, Typography } from '@one-thd/sui-atomic-components';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import {
  capitalizeFirstLetter,
  formatDate,
  formatDateWithDayOfWeek,
  getDeliveryDateAvailability,
  isDeliveryTimelineTodayOrTomorrow,
  isDeliveryTimelineToday,
  getShipModeDetails,
  sortedServices,
  groupServicesByType,
  isAPOFPOZipCode,
  getFSDBMThreshold
} from '../../helpers/utils';
import { DeliveryOptionCard } from './DeliveryOptionCard';
import {
  BODFS_DELIVERY,
  DRAWER_HEADER,
  SHIP_TO_HOME,
  TOMORROW_SCHEDULE_DELIVERY_TEXT,
  CHOOSE_OPTION_TEXT,
  EXPRESS_DELIVERY,
  APO_FPO_ELIGIBILITY_TEXT,
} from '../../helpers/constants';
import { PriorityDeliveryOptions } from '../partials/PriorityDeliveryOptions';
import { FSDBMMessage } from '../partials/FSDBMMessage';

export const DeliveryDrawer = ({
  sthServices,
  bodfsService,
  isQuickViewOpen,
  handleOnClose,
  channel,
  showTodayOrTomorrow = null,
  priorityDeliveryOptions = [],
  APOFPOeligible,
  programInclusions
}) => {

  const enableOnDemandDelivery = !!useConfigService('fs:enableOnDemandDelivery');
  const enableFSDBM = !!useConfigService('fs:enableFsdbm');
  const bodfsLatestDelivery = useConfigService('fs:bodfsLatestDelivery') || '8pm';

  const checkTodayTomorrowDate = (startDate, endDate, formatDateWithRange = null) => {
    if (startDate === endDate && showTodayOrTomorrow) {
      return showTodayOrTomorrow(endDate, true, '', formatDate(endDate, false));
    }
    return formatDateWithRange ? formatDateWithRange(startDate, endDate) : '';
  };
  const sthDate = sthServices?.[0]?.deliveryDates?.startDate === sthServices?.[0]?.deliveryDates?.endDate
  && isDeliveryTimelineTodayOrTomorrow(sthServices[0]?.deliveryTimeline)
    ? capitalizeFirstLetter(getDeliveryDateAvailability(sthServices[0]?.deliveryTimeline)?.toLowerCase())
    : checkTodayTomorrowDate(sthServices[0]?.deliveryDates?.startDate,
      sthServices[0]?.deliveryDates?.endDate,
      formatDateWithDayOfWeek);

  const bodfsDateRaw = getDeliveryDateAvailability(bodfsService[0]?.deliveryTimeline)?.toLowerCase();
  const bodfsDate = (bodfsDateRaw === 'later' && bodfsService[0]?.deliveryDates?.endDate)
    ? formatDateWithDayOfWeek(bodfsService[0].deliveryDates.endDate, bodfsService[0].deliveryDates.endDate)
    : capitalizeFirstLetter(bodfsDateRaw);
  const hasSameDayCarDelivery = bodfsService[0]?.hasSameDayCarDelivery || false;
  const sameDayDeliveryChargeValue = bodfsService?.[0]?.sameDayDeliveryCharge;
  const fsdbmThreshold = getFSDBMThreshold(programInclusions);

  const theme = useTheme(DeliveryDrawer);
  const { showDeliveryEst } = theme.props;
  const hideSameDayDelivery = !showDeliveryEst && (isDeliveryTimelineToday(bodfsDate) || hasSameDayCarDelivery);

  // combine, sort and group the services
  const allServices = [...bodfsService, ...sthServices];
  const hasFreeShipping = allServices?.some(service => !!service?.hasFreeShipping);
  const allSortedServices = sortedServices(allServices);
  const groupedServiceType = groupServicesByType(allSortedServices);
  const isBigBulkySthServices = groupedServiceType[SHIP_TO_HOME] && groupedServiceType[SHIP_TO_HOME]?.length > 1;
  const isAPOFPOZip = isAPOFPOZipCode();

  const renderTodayMessage = () => {
    const showFSDBMMessage = enableFSDBM && !!fsdbmThreshold;
    const isSameDayFree = sameDayDeliveryChargeValue === 0;
    return (
      <Typography variant="body-lg" height="normal">
       {showFSDBMMessage && (
          <FSDBMMessage threshold={fsdbmThreshold} isFree={sameDayDeliveryChargeValue === 0} />
        )}
        {!(showFSDBMMessage && isSameDayFree) && (
          <div className="sui-grid sui-grid-cols-12">
            <div className="sui-col-span-10 sui-pb-1">
              {BODFS_DELIVERY}&nbsp;
              <span className="sui-font-bold">
                Today* {enableFSDBM ? " by " + bodfsLatestDelivery : ""}
              </span>
              &nbsp;
            </div>
            <div className="sui-col-span-2 sui-text-end sui-font-bold">
              <span className="sui-text">{`$${sameDayDeliveryChargeValue}`}</span>
            </div>
          </div>
        )}
      </Typography>
    );
  };

  const renderDisplayMessage = (serviceType) => {
    const showFSDBMMessage = groupedServiceType[serviceType]?.[0]?.type === EXPRESS_DELIVERY && groupedServiceType[serviceType]?.[0]?.deliveryTimeline?.toLowerCase() === 'today' && enableFSDBM && !!fsdbmThreshold;
    const isSameDayFree = groupedServiceType[serviceType]?.[0]?.totalCharge === 0;
    return (
      <div data-automation-id="estimatedDate">
        {showFSDBMMessage
          && <FSDBMMessage threshold={fsdbmThreshold} isFree={isSameDayFree}/>
        }
        {!(showFSDBMMessage && isSameDayFree) && (
          <Typography variant="body-lg" height="normal">
            <div className="sui-grid sui-grid-cols-12">
                <div className="sui-col-span-10 sui-pb-1">
                  {BODFS_DELIVERY}&nbsp;
                  {groupedServiceType[serviceType]?.[0]?.type === EXPRESS_DELIVERY ? (
                    <span>
                      <span className="sui-font-bold">{capitalizeFirstLetter(bodfsDate)}</span>
                      {enableFSDBM &&
                        groupedServiceType[serviceType]?.[0]?.deliveryTimeline?.toLowerCase() ===
                          "today" &&
                        ` by ${bodfsLatestDelivery}`}
                    </span>
                  ) : (
                    <span className="sui-font-bold">{sthDate}</span>
                  )}
                  &nbsp;
                  {groupedServiceType[serviceType]?.[0]?.type === EXPRESS_DELIVERY
                    ? TOMORROW_SCHEDULE_DELIVERY_TEXT
                    : ""}
                </div>

              <div className="sui-col-span-2 sui-text-end sui-font-bold">
                {!isBigBulkySthServices || groupedServiceType[serviceType]?.[0]?.type !== SHIP_TO_HOME
                  ? (groupedServiceType[serviceType]?.[0]?.totalCharge === 0
                    ? <span className="sui-text-success">FREE</span>
                    : `$${groupedServiceType[serviceType]?.[0]?.totalCharge}`) : null}
              </div>
            </div>
          </Typography>
        )}
      </div>
    );
  };
  const renderDeliveryDetailsDrawerNote = () => {
    return (
      <div className="sui-border-t-1 sui-border-primary sui-border-solid">
        <Typography variant="body-md" height="normal">
          <div className="sui-grid sui-grid-cols-12">
            <div className="sui-col-span-12 sui-py-2">
              <span className="sui-font-bold">NOTE:</span>&nbsp; Other items in your cart may impact
              your final delivery cost and dates. {hasFreeShipping ? `Free delivery qualification is based on item
              details (size and weight), quantity, delivery address, and the delivery methods
              available for your order. Changes to quantity, item size or weight, or delivery
              location may affect eligibility and pricing.` : ''}
            </div>
          </div>
        </Typography>
      </div>
    );
  };

  const renderAPOFPOEligibilityText = () => {
    return (
      <div className="sui-pb-5">
        <Typography variant="body-md" height="normal">
          <div className="sui-grid sui-grid-cols-12">
            <div className="sui-col-span-12 sui-pb-1">
              <span className="sui-font-bold" data-testid="APOFPOEligibilityText">{APO_FPO_ELIGIBILITY_TEXT}</span>
            </div>
          </div>
        </Typography>
      </div>
    );
  };

  return (
    // temporary fix for the z-index of live chat and feedback button having too high of a z-index
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <>
      <Drawer
        open={isQuickViewOpen}
        onClose={handleOnClose}
        position="right"
        className="z-index-2000000010"
      >
        <DrawerHeader title={DRAWER_HEADER} onClose={handleOnClose} />
        <DrawerBody>
          {APOFPOeligible && renderAPOFPOEligibilityText()}
          {!isAPOFPOZip && allServices?.length > 1 && (
            <div className="sui-pb-5">
              <Typography variant="body-base" weight="bold" height="normal">
                {CHOOSE_OPTION_TEXT}
              </Typography>
            </div>
          )}
            <div data-automation-id="estimatedDelivery">
              {!isAPOFPOZip && enableOnDemandDelivery && showDeliveryEst && <PriorityDeliveryOptions priorityDeliveryOptions={priorityDeliveryOptions} enableFSDBM={enableFSDBM} fsdbmThreshold={fsdbmThreshold} bodfsLatestDelivery={bodfsLatestDelivery}/>}
              {!isAPOFPOZip && !enableOnDemandDelivery && !hideSameDayDelivery && bodfsService?.[0]?.hasSameDayCarDelivery && (
                <div className={!enableFSDBM ? "sui-pb-5" : "sui-border-t-1 sui-border-primary sui-border-solid sui-py-2"}>
                  {renderTodayMessage()}
                </div>
              )}
              {!isAPOFPOZip && Object.keys(groupedServiceType).map((serviceType) => (
                <div key={serviceType}>
                  <div className={!enableFSDBM ? (serviceType === SHIP_TO_HOME && serviceType.length > 1 ? 'sui-pb-2' : 'sui-pb-5') : "sui-border-t-1 sui-border-primary sui-border-solid sui-py-4"}>
                    {renderDisplayMessage(serviceType)}
                  </div>
                  <div data-automation-id="cardContainer">
                    {groupedServiceType[serviceType]?.map((service) => (
                      service.type === SHIP_TO_HOME && (
                        <DeliveryOptionCard
                          channel={channel}
                          deliveryType={SHIP_TO_HOME}
                          deliveryOption={service}
                          isBigBulkySthServices={isBigBulkySthServices}
                          enableFSDBM={enableFSDBM}
                        />
                      )
                    ))}
                  </div>
                </div>
              ))}
              {renderDeliveryDetailsDrawerNote()}
            </div>
          {bodfsService?.length > 0 && (
            <div data-automation-id="drawerFooter" className="sui-pt-3">
              <Typography variant="body-small" height="normal">
                <div className="sui-pb-1">
                  <span className="sui-text-sm">
                    {getShipModeDetails()}
                  </span>
                </div>
              </Typography>
            </div>
          )}
        </DrawerBody>
      </Drawer>
    </>
  );
};
DeliveryDrawer.propTypes = {
  isQuickViewOpen: PropTypes.bool,
  handleOnClose: PropTypes.func,
  sthServices: PropTypes.instanceOf(Array).isRequired,
  bodfsService: PropTypes.instanceOf(Array).isRequired,
  channel: PropTypes.string,
  showTodayOrTomorrow: PropTypes.func,
  priorityDeliveryOptions: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    timeline: PropTypes.string,
    type: PropTypes.string,
    totalCharge: PropTypes.number,
  })),
  APOFPOeligible: PropTypes.bool,
};
DeliveryDrawer.defaultProps = {
  isQuickViewOpen: false,
  handleOnClose: () => {
  },
  channel: null,
  showTodayOrTomorrow: null,
  priorityDeliveryOptions: [],
  APOFPOeligible: false,
};
DeliveryDrawer.themeProps = {
  showDeliveryEst: Boolean,
};
DeliveryDrawer.defaultThemeProps = {
  showDeliveryEst: true,
};
DeliveryDrawer.displayName = 'DeliveryDrawer';